import { areArraysEqual } from '@mui/base';
import {
  AutocompleteHighlightChangeReason,
  AutocompleteInputChangeReason,
} from '@mui/base/useAutocomplete';
import {
  AutocompleteOwnerState,
  AutocompleteRenderOptionState,
  ChipTypeMap,
  Autocomplete as MuiAutocomplete,
  type AutocompleteProps as MuiAutocompleteProps,
} from '@mui/material';
import React, { useEffect, useRef } from 'react';
import {
  FixedContentProps,
  FixedPaper,
  FixedPaperContext,
} from 'src/components/Menu/FixedMenuContent';

function AutocompleteRoot<
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent']
>(
  props: AutocompleteProps<
    Value,
    Multiple,
    DisableClearable,
    FreeSolo,
    ChipComponent
  >,
  forwardedRef: React.Ref<unknown>
): React.ReactElement {
  const {
    value: valueProp,
    renderOption,
    renderOptionWithOwnerState,
    fixedHeader,
    fixedFooter,
    ...rest
  } = props;

  const hasFixedContent = fixedHeader != null || fixedFooter != null;

  // Work around FED-1493 by preventing new arrays with the same values
  // (which always happens when rendered from Dart, even when memoized;
  // see ticket for more info)
  // from triggering https://github.com/mui/material-ui/issues/32425.
  //
  // Note that removing this behavior would be a breaking change,
  // since consumers that don't memoize value would start getting this bad behavior.
  const lastValue = useRef(valueProp);
  const value =
    valueProp !== lastValue.current &&
    Array.isArray(valueProp) &&
    Array.isArray(lastValue.current) &&
    areArraysEqual(valueProp, lastValue.current)
      ? lastValue.current
      : valueProp;
  useEffect(() => {
    lastValue.current = value;
  }, [value]);

  return (
    <FixedPaperContext.Provider
      value={{ fixedHeader, fixedFooter, popoverPaper: false }}
    >
      <MuiAutocomplete<
        Value,
        Multiple,
        DisableClearable,
        FreeSolo,
        ChipComponent
      >
        ref={forwardedRef}
        {...rest}
        value={value}
        slots={{
          ...(hasFixedContent && { paper: FixedPaper }),
          ...rest.slots,
        }}
        {...((renderOption ?? renderOptionWithOwnerState) != null && {
          renderOption: (
            props: React.HTMLAttributes<HTMLLIElement>,
            option: Value,
            state: AutocompleteRenderOptionState,
            ownerState: AutocompleteOwnerState<
              Value,
              Multiple,
              DisableClearable,
              FreeSolo,
              ChipComponent
            >
          ) => {
            if (renderOption) {
              return renderOption(props, option, state);
            }
            if (renderOptionWithOwnerState) {
              return renderOptionWithOwnerState(
                props,
                option,
                state,
                ownerState
              );
            }
          },
        })}
      />
    </FixedPaperContext.Provider>
  );
}

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const Autocomplete = React.forwardRef(
  AutocompleteRoot
) as typeof AutocompleteRoot;

export default Autocomplete;

/**
 * Deprecate props that aren't supported in Unify.
 */
type UnsupportedProps<
  Value,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  ChipComponent extends React.ElementType = 'div'
> = {
  /**
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  popupIcon?: MuiAutocompleteProps<
    Value,
    Multiple,
    DisableClearable,
    FreeSolo,
    ChipComponent
  >['popupIcon'];

  /**
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  clearIcon?: MuiAutocompleteProps<
    Value,
    Multiple,
    DisableClearable,
    FreeSolo,
    ChipComponent
  >['clearIcon'];
};

// This custom implementation of AutocompleteProps is used to override the `renderOption` prop signature so that
// it doesn't break existing Dart consumers and to add `renderOptionWithOwnerState` as the alternative.
export type AutocompleteProps<
  Value,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  ChipComponent extends React.ElementType = 'div'
> = Omit<
  MuiAutocompleteProps<
    Value,
    Multiple,
    DisableClearable,
    FreeSolo,
    ChipComponent
  >,
  | 'renderOption'
  | 'onKeyDown'
  | 'onHighlightChange'
  | 'onInputChange'
  // TODO FED-4283: Remove renderTags when we add wrapRenderValue to the public API
  | 'renderTags'
  | keyof UnsupportedProps<
      Value,
      Multiple,
      DisableClearable,
      FreeSolo,
      ChipComponent
    >
> &
  FixedContentProps & {
    /**
     * Render the selected value when doing multiple selections.
     */
    // TODO FED-4283: Remove when we add wrapRenderValue to the dart public API
    // This is here to keep it undeprecated until we add the new `renderValue` prop
    renderTags?: MuiAutocompleteProps<
      Value,
      Multiple,
      DisableClearable,
      FreeSolo,
      ChipComponent
    >['renderTags'];
    /**
     * Render the option, use `getOptionLabel` by default.
     *
     * For access to the `ownerState` as an additional arg, use `renderOptionWithOwnerState`.
     *
     * @param {object} props The props to apply on the li element.
     * @param {Value} option The option to render.
     * @param {object} state The state of each option.
     * @returns {ReactNode}
     */
    renderOption?: (
      props: React.HTMLAttributes<HTMLLIElement>,
      option: Value,
      state: AutocompleteRenderOptionState
    ) => React.ReactNode;

    /**
     * Render the option, use `getOptionLabel` by default.
     *
     * @param {object} props The props to apply on the li element.
     * @param {Value} option The option to render.
     * @param {object} state The state of each option.
     * @param {object} ownerState The state of the Autocomplete component.
     * @returns {ReactNode}
     */
    renderOptionWithOwnerState?: (
      props: React.HTMLAttributes<HTMLLIElement>,
      option: Value,
      state: AutocompleteRenderOptionState,
      ownerState: AutocompleteOwnerState<
        Value,
        Multiple,
        DisableClearable,
        FreeSolo,
        ChipComponent
      >
    ) => React.ReactNode;

    // Override the nullability of the `event` param to work around https://github.com/mui/material-ui/issues/43213
    /**
     * Callback fired when the highlight option changes.
     *
     * @param {React.SyntheticEvent} event The event source of the callback.
     * @param {Value} option The highlighted option.
     * @param {string} reason Can be: `"keyboard"`, `"auto"`, `"mouse"`, `"touch"`.
     */
    onHighlightChange?: (
      event: React.SyntheticEvent | undefined,
      option: Value | null,
      reason: AutocompleteHighlightChangeReason
    ) => void;

    // Override the nullability of the `event` param to work around https://github.com/mui/material-ui/issues/43899
    /**
     * Callback fired when the input value changes.
     *
     * @param {React.SyntheticEvent} event The event source of the callback.
     * @param {string} value The new value of the text input.
     * @param {string} reason Can be: `"input"` (user input), `"reset"` (programmatic change), `"clear"`.
     */
    onInputChange?: (
      event: React.SyntheticEvent | null,
      value: string,
      reason: AutocompleteInputChangeReason
    ) => void;
  } & UnsupportedProps<
    Value,
    Multiple,
    DisableClearable,
    FreeSolo,
    ChipComponent
  >;
